import React, { useState, useEffect } from 'react';
import { generateEventSlug, isEventCanceled, parseEventDate } from '../utils/eventUtils';
import SafeMarkdown from './SafeMarkdown';

const EventDetails = ({ event, onClose, theme = {} }) => {
    const startDate = parseEventDate(event.startDate);
    const endDate = parseEventDate(event.endDate || event.startDate);
    const safeText = (value, fallback = '') => {
        if (typeof value === 'string' || typeof value === 'number') {
            return String(value);
        }
        return fallback;
    };
    const descriptionText = typeof event.description === 'string' ? event.description : '';
    const tags = Array.isArray(event.tags)
        ? event.tags
        : typeof event.tags === 'string'
            ? event.tags.split(',').map(tag => ({ name: tag.trim() })).filter(tag => tag.name)
            : [];

    // Default theme values if not provided
    const defaultTheme = {
        text: 'text-gray-900',
        secondaryText: 'text-gray-600',
        link: 'text-blue-600 hover:text-blue-800',
        tag: 'bg-blue-100 text-blue-800',
        border: 'border-gray-200'
    };

    // Merge provided theme with defaults
    const currentTheme = { ...defaultTheme, ...theme };

    // Starred events state
    const [isStarred, setIsStarred] = useState(false);
    const eventSlug = generateEventSlug(event);

    // Check if this event is starred on mount
    useEffect(() => {
        const starred = JSON.parse(localStorage.getItem('starredEvents') || '[]');
        setIsStarred(starred.includes(eventSlug));
    }, [eventSlug]);

    // Toggle star
    const handleToggleStar = () => {
        let starred = JSON.parse(localStorage.getItem('starredEvents') || '[]');
        if (starred.includes(eventSlug)) {
            starred = starred.filter(slug => slug !== eventSlug);
            setIsStarred(false);
        } else {
            starred.push(eventSlug);
            setIsStarred(true);
        }
        localStorage.setItem('starredEvents', JSON.stringify(starred));
    };

    const handleShare = () => {
        const slug = generateEventSlug(event);
        const url = `${window.location.origin}/#${slug}`;
        navigator.clipboard.writeText('Check out this event I found on ATLTech.events: ' + url);
    };

    const canceled = isEventCanceled(event);

    return (
        <div className="p-4 items-center">
            <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                    {canceled && (
                        <p className="bg-gray-300 text-gray-800 text-xs px-2 py-1 font-bold rounded-full mb-2 inline-block">
                            <i className="fas fa-ban mr-1" aria-hidden="true"></i>Canceled
                        </p>
                    )}
                    {event.eventType === 'Featured' && (
                        <p className="bg-yellow-400 text-black text-xs px-2 py-1 font-bold rounded-full mb-2 inline-block">Featured Event</p>
                    )}
                </div>
                <button
                    onClick={handleToggleStar}
                    aria-label={isStarred ? 'Unfavorite this event' : 'Favorite this event'}
                    className="text-red-500 text-xl focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-400 rounded hover:scale-110 transition-transform"
                    style={{ minWidth: 32 }}
                >
                    <i className={isStarred ? 'fas fa-heart' : 'far fa-heart'} aria-hidden="true"></i>
                </button>
            </div>
            <h2 className={`text-lg font-bold mb-4 ${canceled ? 'line-through text-gray-500' : ''}`}>{safeText(event.title, 'Event Details')}</h2>
            <div className="mb-4 max-h-32 overflow-y-auto">
                <SafeMarkdown text={descriptionText} />
            </div>
            <p className="mb-4"><strong>Organization:</strong> {safeText(event.organization, 'Unknown organization')}</p>
            <p className="mb-4"><strong>Date:</strong> {startDate ? startDate.toLocaleString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : 'TBD'}</p>
            {startDate && endDate && (startDate.getHours() !== 0 || startDate.getMinutes() !== 0) && (
                <p className="mb-4"><strong>Time:</strong> {startDate.toLocaleString(undefined, { hour: '2-digit', minute: '2-digit' })} to {endDate.toLocaleString(undefined, { hour: '2-digit', minute: '2-digit' })}</p>
            )}
            {event.location && (
                <p className="mb-4"><strong>Location:</strong> {safeText(event.location)}</p>
            )}
            {tags.length > 0 && (
                <div className="mb-4 text-center">
                    <div className="flex flex-wrap gap-2 mt-2 justify-center">
                        {tags.map((tag, index) => (
                            <span 
                                key={index} 
                                className={`${currentTheme.tag} text-xs font-medium px-2.5 py-0.5 rounded`}
                            >
                                {typeof tag === 'string' ? tag : tag?.name || ''}
                            </span>
                        ))}
                    </div>
                </div>
            )}
            <div className="text-center py-3">
                {canceled ? (
                    <span className="inline-block bg-gray-200 text-gray-500 py-2 px-4 rounded" aria-label="This event has been canceled">
                        <i className="fas fa-ban mr-2" aria-hidden="true"></i>Event Canceled
                    </span>
                ) : event.eventURL && (() => {
                    const eventEndDate = parseEventDate(event.endDate || event.startDate);
                    if (!eventEndDate) {
                        return false;
                    }
                    const today = new Date();
                    today.setHours(0, 0, 0, 0);
                    return eventEndDate >= today;
                })() && (
                    <a
                        href={event.eventURL}
                        className={`inline-block ${currentTheme.button} py-2 px-4 rounded transition-colors`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {event.eventURL === '/private-events' ? 'About private events' : 'Go to event page'}
                    </a>
                )}
            </div>
            {event.sponsorLogo && (
                <div className="mt-2 text-center mb-2">
                    <span className={`text-xs ${currentTheme.secondaryText}`}>Event Hosted By</span>
                    <a href={event.sponsorURL} target="_blank" rel="noopener noreferrer" className="block mt-1">
                        <img src={event.sponsorLogo} alt={event.sponsorName || 'Event Host'} className="max-h-8 mx-auto" />
                    </a>
                </div>
            )}

            <div className={`block text-center px-4 py-2 border-t ${currentTheme.border}`}>
                <button
                    onClick={handleShare}
                    className={`${currentTheme.link} text-sm text-center gap-2`}
                >
                    <i className="fas fa-share-alt"></i>&nbsp;
                    Share This (Copy to Clipboard)
                </button>
            </div>
        </div>
    );
};

export default EventDetails;